
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlatformService, PayoutStats, PayoutLedgerItem } from '../../../services/platform.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-clearing-house',
  standalone: true,
  imports: [CommonModule, DecimalPipe, DatePipe, FormsModule],
  templateUrl: './clearing-house.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClearingHouseComponent implements OnInit {
  private platformService = inject(PlatformService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);

  // Data Signals
  stats = signal<PayoutStats | null>(null);
  ledger = signal<PayoutLedgerItem[]>([]);
  isLoading = signal(false);
  filter = signal<'PENDING' | 'PAID' | 'ALL'>('PENDING');
  now = signal(new Date()); 
  
  // UI Signals
  isGroupView = signal(false);
  selection = signal<Set<string>>(new Set());
  
  // Modal Signals
  isPaymentModalOpen = signal(false);
  isProcessing = signal(false);
  
  // Payment Form Data
  paymentBankRef = signal('');
  paymentSlipFile = signal<File | null>(null);

  // Security Check: Is current user an Auditor (Read-Only)?
  isReadOnly = computed(() => {
      const role = this.authService.currentUser()?.role;
      return role === 'platform_auditor';
  });
  
  // Computed: Grouped Ledger (For Clinic Aggregation)
  groupedLedger = computed(() => {
    if (!this.isGroupView()) return [];

    const groups = new Map<string, { 
      payee_id: string, 
      payee_name: string, 
      bank_account: any, 
      total_amount: number, 
      count: number,
      ids: string[] 
    }>();

    this.ledger().forEach(item => {
      const key = item.payee_id;
      if (!groups.has(key)) {
        groups.set(key, {
          payee_id: item.payee_id,
          payee_name: item.payee_name,
          bank_account: item.bank_account,
          total_amount: 0,
          count: 0,
          ids: []
        });
      }
      const group = groups.get(key)!;
      group.total_amount += item.amount;
      group.count++;
      group.ids.push(item.id);
    });

    return Array.from(groups.values());
  });

  // Computed: Selection Summary
  selectedAmount = computed(() => {
    let total = 0;
    const selectedIds = this.selection();
    this.ledger().forEach(item => {
      if (selectedIds.has(item.id)) {
        total += item.amount;
      }
    });
    return total;
  });

  selectedCount = computed(() => this.selection().size);

  ngOnInit() {
    this.loadData();
  }

  async loadData() {
    this.isLoading.set(true);
    this.selection.set(new Set()); // Reset selection on reload
    try {
      const [statsData, ledgerData] = await Promise.all([
        this.platformService.getPayoutStats(),
        this.platformService.getPayoutLedger(this.filter())
      ]);
      this.stats.set(statsData);
      this.ledger.set(ledgerData);
      this.now.set(new Date()); // Update timestamp
    } catch (e: any) {
      this.notificationService.showError("Failed to load clearing data: " + e.message);
    } finally {
      this.isLoading.set(false);
    }
  }

  setFilter(val: 'PENDING' | 'PAID' | 'ALL') {
    this.filter.set(val);
    this.loadData();
  }

  // --- View Toggles ---
  toggleView() {
    this.isGroupView.update(v => !v);
    this.selection.set(new Set()); // Reset selection when view changes
  }

  // --- Selection Logic ---
  toggleSelectAll(event: Event) {
    if (this.isReadOnly()) return; // Prevent selection if read-only

    const isChecked = (event.target as HTMLInputElement).checked;
    const newSet = new Set<string>();
    
    if (isChecked) {
      if (this.isGroupView()) {
         // Logic for group view selection if needed
      } else {
         this.ledger().forEach(item => newSet.add(item.id));
      }
    }
    this.selection.set(newSet);
  }
  
  toggleItem(id: string) {
    if (this.isReadOnly()) return; // Prevent selection if read-only

    this.selection.update(set => {
      const newSet = new Set(set);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }
  
  isAllSelected() {
    if (this.ledger().length === 0) return false;
    return this.selection().size === this.ledger().length;
  }
  
  isIndeterminate() {
    return this.selection().size > 0 && this.selection().size < this.ledger().length;
  }

  // --- Payment Modal Actions ---
  
  openBatchPaymentModal() {
    if (this.selection().size === 0 || this.isReadOnly()) return;
    this.paymentBankRef.set('');
    this.paymentSlipFile.set(null);
    this.isPaymentModalOpen.set(true);
  }

  openGroupPaymentModal(group: any) {
    if (this.isReadOnly()) return;

    // Select all items in this group
    const newSet = new Set<string>();
    group.ids.forEach((id: string) => newSet.add(id));
    this.selection.set(newSet);
    
    this.openBatchPaymentModal();
  }
  
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.paymentSlipFile.set(input.files[0]);
    }
  }
  
  closePaymentModal() {
    this.isPaymentModalOpen.set(false);
  }

  async confirmPayment() {
    if (this.selection().size === 0 || this.isReadOnly()) return;
    
    this.isProcessing.set(true);
    try {
      let proofUrl = '';
      const file = this.paymentSlipFile();
      
      // Upload Slip if provided
      if (file) {
        proofUrl = await this.platformService.uploadPayoutSlip(file);
      }

      // Execute Batch Payment
      await this.platformService.batchMarkPayoutPaid(
        Array.from(this.selection()),
        proofUrl,
        this.paymentBankRef()
      );

      this.notificationService.showSuccess(`ทำรายการสำเร็จ ${this.selection().size} รายการ`);
      this.closePaymentModal();
      this.loadData();
    } catch(e: any) {
      this.notificationService.showError("เกิดข้อผิดพลาด: " + e.message);
    } finally {
      this.isProcessing.set(false);
    }
  }

  getUniquePayeesInSelection() {
    const payees = new Map<string, any>();
    const selectedIds = this.selection();
    
    this.ledger().forEach(item => {
        if (selectedIds.has(item.id)) {
            payees.set(item.payee_id, { name: item.payee_name, bank: item.bank_account });
        }
    });
    return Array.from(payees.values());
  }
}
